// src/pages/LoginPage.tsx
// VERSÃO CORRIGIDA - Com MFA + refresh token

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import './AuthPagesGRAIA.css';

const LoginPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();
    const { login, isAuthenticated } = useAuth();
    const location = useLocation();

    useEffect(() => {
        if (isAuthenticated) {
            const params = new URLSearchParams(location.search);
            const inviteCode = params.get('invite');
            if (inviteCode) {
                navigate(`/invite/${inviteCode}`);
            } else {
                navigate('/dashboard');
            }
        }
    }, [isAuthenticated, navigate, location.search]);

    // Mostrar mensagem de manutenção se redirecionado pelo Google OAuth bloqueado
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('error') === 'maintenance') {
            setError('A plataforma está em manutenção. Disponível apenas para acesso administrativo.');
        }
    }, [location.search]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password, platform: 'enem' })
            });

            const data = await response.json();

            // Modo de manutenção — só admin pode entrar
            if (data.maintenanceMode) {
                setError(data.message || 'A plataforma está em manutenção. Disponível apenas para acesso administrativo.');
                return;
            }

            // ✅ VERIFICAR SE PRECISA DE MFA (ANTES do response.ok)
            if (data.requiresMFA) {
                navigate('/mfa/verify', {
                    state: {
                        email,
                        password,
                        userId: data.userId,
                        platform: 'enem'
                    }
                });
                return;
            }

            if (response.ok) {
                // ✅ PASSAR AMBOS OS TOKENS + USER DATA
                login(data.accessToken, data.refreshToken, {
                    userId: data.userId,
                    username: data.username,
                    email: data.email || email,
                    role: data.role
                });

                const locationState = location.state as any;
                const params = new URLSearchParams(location.search);
                const inviteCode = params.get('invite');

                if (inviteCode) {
                    navigate(`/invite/${inviteCode}`);
                } else if (locationState?.from === 'checkout' && locationState?.product && locationState?.selectedPlan) {
                    navigate('/checkout', { state: { product: locationState.product, selectedPlan: locationState.selectedPlan } });
                } else {
                    navigate('/dashboard');
                }
            } else {
                setError(data.message || 'Email ou senha incorretos');
            }
        } catch (err) {
            setError('Erro ao conectar ao servidor');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError('');

        try {
            window.location.href = '/api/auth/google';
        } catch (err) {
            setError('Erro ao iniciar login com Google');
            setLoading(false);
        }
    };

    return (
        <div className="graia-auth-wrapper">
            {/* COLUNA ESQUERDA - IDENTIDADE */}
            <div className="graia-auth-identity">
                <div className="graia-auth-identity-content">
                    <div className="graia-auth-logo-section">
                        <img
                            src="/graia-logo.png"
                            alt="GRAIA Logo"
                            className="graia-auth-logo-img"
                        />
                        <h1 className="graia-auth-brand">GRAIA</h1>
                        <p className="graia-auth-tagline">Estudo estruturado. Conhecimento consolidado.</p>
                    </div>
                </div>
            </div>

            {/* COLUNA DIREITA - FORMULÁRIO */}
            <div className="graia-auth-form-area">
                <div className="graia-auth-form-container">
                    <div className="graia-auth-card">
                        <div className="graia-auth-header">
                            <h2>Acessar plataforma</h2>
                            <p>Entre com suas credenciais</p>
                        </div>

                        {error && (
                            <div className="graia-auth-alert graia-auth-alert-error">
                                <AlertCircle size={16} />
                                <span>{error}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="graia-auth-form">

                            {/* ✅ CAMPO EMAIL - corrigido: ícone Mail, type email, value={email} */}
                            <div className="graia-auth-field">
                                <label className="graia-auth-label">Email</label>
                                <div className="graia-auth-input-wrapper">
                                    <Mail size={16} className="graia-auth-input-icon" />
                                    <input
                                        type="email"
                                        className="graia-auth-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="seu@email.com"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* ✅ CAMPO SENHA - corrigido: ícone Lock, type password/text, value={password} */}
                            <div className="graia-auth-field">
                                <label className="graia-auth-label">Senha</label>
                                <div className="graia-auth-input-wrapper">
                                    <Lock size={16} className="graia-auth-input-icon" />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="graia-auth-input graia-auth-input-with-toggle"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Sua senha"
                                        required
                                        disabled={loading}
                                        minLength={6}
                                    />
                                    <button
                                        type="button"
                                        className="graia-auth-toggle-password"
                                        onClick={() => setShowPassword(p => !p)}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            <div className="graia-auth-forgot">
                                <Link to="/forgot-password">Esqueceu sua senha?</Link>
                            </div>

                            <button
                                type="submit"
                                className="graia-auth-btn-primary"
                                disabled={loading}
                            >
                                {loading ? 'Acessando...' : 'Acessar'}
                            </button>
                        </form>

                        <div className="graia-auth-divider">
                            <span>Ou continue com</span>
                        </div>

                        <button
                            type="button"
                            className="graia-auth-btn-google"
                            onClick={handleGoogleLogin}
                            disabled={loading}
                        >
                            <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
                                <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853" />
                                <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                                <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335" />
                            </svg>
                            Google
                        </button>

                        <div className="graia-auth-footer">
                            <p>
                                Não possui acesso?{' '}
                                <Link to="/cadastro">Criar conta</Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;